import { useMemo } from "react";
import { AthleteVisualizationSelect } from "@/components/athlete/AthleteVisualizationSelect";
import {
  formatDateTick,
  formatSplitSeconds,
} from "@/components/athlete/formatters";
import type {
  SplitProgressionData,
  SplitProgressionPoint,
} from "@/components/athlete/types";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

type SplitSeriesKey = "swimSeconds" | "bikeSeconds" | "runSeconds";
type SplitSeries = {
  key: SplitSeriesKey;
  label: string;
  formattedKey: "swimFormatted" | "bikeFormatted" | "runFormatted";
  color: string;
};

const SPLIT_SERIES: SplitSeries[] = [
  {
    key: "swimSeconds",
    label: "Swim",
    formattedKey: "swimFormatted",
    color: "#06b6d4",
  },
  {
    key: "bikeSeconds",
    label: "Bike",
    formattedKey: "bikeFormatted",
    color: "#f97316",
  },
  {
    key: "runSeconds",
    label: "Run",
    formattedKey: "runFormatted",
    color: "#ec4899",
  },
];

export function SplitProgressionPanel({
  progression,
  isLoading,
  error,
  selectedSeriesId,
  onSeriesChange,
  showSeriesSelect = true,
}: {
  progression: SplitProgressionData | undefined;
  isLoading: boolean;
  error: unknown;
  selectedSeriesId: string | undefined;
  onSeriesChange: (value: string) => void;
  showSeriesSelect?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="athlete-trend-empty">
        Loading split progression data...
      </div>
    );
  }

  if (error) {
    return (
      <div className="athlete-trend-empty">
        Split progression is unavailable right now.
      </div>
    );
  }

  if (!progression || progression.availableSeries.length === 0) {
    return (
      <div className="athlete-trend-empty">
        No race series history available for split progression yet.
      </div>
    );
  }

  const selected =
    progression.availableSeries.find(
      (series) => series.id === selectedSeriesId,
    ) ?? progression.selectedSeries;
  const selectedValue =
    selectedSeriesId ??
    progression.selectedSeriesId ??
    progression.availableSeries[0]?.id ??
    "";

  return (
    <>
      <div className="athlete-trend-header">
        <div>
          <h3 className="athlete-panel-title">Split Progression</h3>
          {selected && (
            <p className="athlete-split-series-meta">
              {selected.raceCount} races tracked
              {selected.firstYear != null &&
                selected.lastYear != null &&
                ` · ${selected.firstYear}-${selected.lastYear}`}
            </p>
          )}
        </div>
        {showSeriesSelect ? (
          <AthleteVisualizationSelect
            label="Race series"
            value={selectedValue}
            placeholder="Select series"
            options={progression.availableSeries.map((series) => ({
              value: series.id,
              label: series.displayName,
            }))}
            onValueChange={onSeriesChange}
          />
        ) : null}
      </div>
      <SplitProgressionChart points={progression.points} />
    </>
  );
}

function SplitProgressionChart({
  points,
}: {
  points: SplitProgressionPoint[];
}) {
  const chartData = useMemo(() => {
    const lastIdx = points.length - 1;
    const step =
      points.length > 5 ? Math.max(1, Math.floor(points.length / 4)) : 1;

    return points.map((point, index) => {
      const showLabel =
        points.length <= 5 ||
        index === 0 ||
        index === lastIdx ||
        index % step === 0;
      return {
        ...point,
        xLabel: showLabel
          ? formatDateTick(point.eventDate, point.year, index)
          : "",
      };
    });
  }, [points]);

  const hasAnySplitValues = SPLIT_SERIES.some((series) =>
    points.some((point) => point[series.key] != null),
  );

  if (points.length === 0) {
    return (
      <div className="athlete-trend-empty">
        No race points available for this series.
      </div>
    );
  }

  if (!hasAnySplitValues) {
    return (
      <div className="athlete-trend-empty">
        No swim, bike, or run split times are available for this series yet.
      </div>
    );
  }

  return (
    <div className="athlete-split-mini-grid">
      {SPLIT_SERIES.map((series) => {
        const hasValues = points.some((point) => point[series.key] != null);
        const splitValues = points
          .map((point) => point[series.key])
          .filter((value): value is number => value != null);
        const yDomain =
          splitValues.length === 0
            ? ([0, 10] as const)
            : (() => {
                const minTime = Math.min(...splitValues);
                const maxTime = Math.max(...splitValues);
                const spread = Math.max(6, Math.ceil((maxTime - minTime) * 0.2));
                return [
                  Math.max(0, minTime - spread),
                  maxTime + spread,
                ] as const;
              })();
        const config = {
          [series.key]: {
            label: series.label,
            color: series.color,
          },
        } satisfies ChartConfig;

        return (
          <div key={series.key} className="athlete-split-mini-card">
            <div className="athlete-split-mini-header">
              <span
                className="athlete-split-mini-dot"
                style={{ backgroundColor: series.color }}
              />
              <h4 className="athlete-split-mini-title">{series.label}</h4>
            </div>
            {hasValues ? (
              <ChartContainer
                config={config}
                className="mt-2 aspect-auto! h-[220px] min-h-[220px]"
              >
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 10, left: 10, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="xLabel"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={16}
                    interval={0}
                  />
                  <YAxis
                    type="number"
                    domain={yDomain}
                    tickFormatter={(value) => formatSplitSeconds(Number(value))}
                    tickLine={false}
                    axisLine={false}
                    width={74}
                  />
                  <ChartTooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(_label, payload) =>
                          (payload?.[0]?.payload as { eventName?: string } | undefined)
                            ?.eventName ?? "Event"
                        }
                        formatter={(_value, _name, item) => {
                          const row = item.payload as SplitProgressionPoint;
                          return row[series.formattedKey] ?? "—";
                        }}
                      />
                    }
                  />
                  <Line
                    dataKey={series.key}
                    type="monotone"
                    stroke={`var(--color-${series.key})`}
                    strokeWidth={2.4}
                    dot={{
                      r: 3,
                      fill: `var(--color-${series.key})`,
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 5,
                      fill: `var(--color-${series.key})`,
                      strokeWidth: 2,
                    }}
                    connectNulls={false}
                  />
                </LineChart>
              </ChartContainer>
            ) : (
              <div className="athlete-trend-empty">No {series.label.toLowerCase()} data</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
